const { Permission: P } = require('./permissions');

const RoleId = Object.freeze({
  ADMIN: 1,
  CLIENT: 2,
  ACCOUNT_VIEWER: 3,
  VIEWER: 4,
  AGENCY: 5,
  DEVELOPER: 6,
  ACCOUNTANT: 7,
  MARKETER: 8,
  MARKETER_VIEW_ONLY: 9,
  RESTRICTED: 10,
});

const admin = Object.freeze({
  name: RoleId.ADMIN,
  description: 'ImageKit Administrator',
  helptext: 'Has access to all ImageKit accounts',
  permissions: Object.freeze([
    P.USER_VIEW,
    P.USER_MANAGE,
    P.CLIENT_VIEW,
    P.CLIENT_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
    P.SELF_CLIENT_VIEW,
    P.SELF_CLIENT_MANAGE,
    P.ADMIN_PANEL_VIEW,
    P.ADMIN_PANEL_MANAGE,
    P.SELF_ANALYTICS_VIEW,
    P.SELF_ANALYTICS_MANAGE,
    P.SELF_BILLING_VIEW,
    P.SELF_BILLING_MANAGE,
    P.SELF_CLEAR_CACHE_VIEW,
    P.SELF_CLEAR_CACHE_MANAGE,
    P.SELF_DEVELOPERS_VIEW,
    P.SELF_DEVELOPERS_MANAGE,
    P.SELF_IMAGE_SETTINGS_VIEW,
    P.SELF_IMAGE_SETTINGS_MANAGE,
    P.SELF_INTEGRATION_VIEW,
    P.SELF_INTEGRATION_MANAGE,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_PERFORMANCE_VIEW,
    P.SELF_PERFORMANCE_MANAGE,
    P.SELF_REFERRAL_VIEW,
    P.SELF_REFERRAL_MANAGE,
    P.SELF_TEAM_VIEW,
    P.SELF_TEAM_MANAGE,
    P.SELF_USAGE_VIEW,
    P.SELF_USAGE_MANAGE,
    P.SELF_CHILD_VIEW,
    P.SELF_CHILD_MANAGE,
  ]),
});

const client = Object.freeze({
  name: RoleId.CLIENT,
  description: 'Account Administrator',
  helptext: 'Full access to everything including user management',
  permissions: Object.freeze([
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
    P.SELF_CLIENT_VIEW,
    P.SELF_CLIENT_MANAGE,
    P.SELF_ANALYTICS_VIEW,
    P.SELF_ANALYTICS_MANAGE,
    P.SELF_BILLING_VIEW,
    P.SELF_BILLING_MANAGE,
    P.SELF_CLEAR_CACHE_VIEW,
    P.SELF_CLEAR_CACHE_MANAGE,
    P.SELF_DEVELOPERS_VIEW,
    P.SELF_DEVELOPERS_MANAGE,
    P.SELF_IMAGE_SETTINGS_VIEW,
    P.SELF_IMAGE_SETTINGS_MANAGE,
    P.SELF_INTEGRATION_VIEW,
    P.SELF_INTEGRATION_MANAGE,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_PERFORMANCE_VIEW,
    P.SELF_PERFORMANCE_MANAGE,
    P.SELF_REFERRAL_VIEW,
    P.SELF_REFERRAL_MANAGE,
    P.SELF_TEAM_VIEW,
    P.SELF_TEAM_MANAGE,
    P.SELF_USAGE_VIEW,
    P.SELF_USAGE_MANAGE,
  ]),
});

const accountViewer = Object.freeze({
  name: RoleId.ACCOUNT_VIEWER,
  description: 'Account Viewer',
  helptext: 'Has view-only access on this account',
  permissions: Object.freeze([P.SELF_USER_VIEW]),
});

const viewer = Object.freeze({
  name: RoleId.VIEWER,
  description: 'ImageKit Viewer',
  helptext: 'Has view-only access to all ImageKit accounts',
  permissions: Object.freeze([
    P.USER_VIEW,
    P.CLIENT_VIEW,
    P.SELF_USER_VIEW,
    P.SELF_CLIENT_VIEW,
    P.SELF_INTEGRATION_VIEW,
    P.SELF_IMAGE_SETTINGS_VIEW,
    P.SELF_CLEAR_CACHE_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_BILLING_VIEW,
    P.SELF_CHILD_VIEW,
    P.SELF_TEAM_VIEW,
    P.SELF_USAGE_VIEW,
    P.SELF_PERFORMANCE_VIEW,
    P.SELF_REFERRAL_VIEW,
    P.SELF_DEVELOPERS_VIEW,
    P.SELF_ANALYTICS_VIEW,
  ]),
});

const agency = Object.freeze({
  name: RoleId.AGENCY,
  description: 'Agency Administrator',
  helptext: 'Agency access to all child accounts belonging to this agency',
  permissions: Object.freeze([
    P.SELF_AGENCY_ANALYTICS_VIEW,
    P.SELF_AGENCY_ANALYTICS_MANAGE,
    P.SELF_BILLING_VIEW,
    P.SELF_BILLING_MANAGE,
    P.SELF_CHILD_VIEW,
    P.SELF_CHILD_MANAGE,
    P.SELF_CLIENT_VIEW,
    P.SELF_CLIENT_MANAGE,
    P.SELF_USAGE_VIEW,
    P.SELF_USAGE_MANAGE,
    P.SELF_TEAM_VIEW,
    P.SELF_TEAM_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const developer = Object.freeze({
  name: RoleId.DEVELOPER,
  description: 'Developer',
  helptext: 'Access to everything except user management and billing',
  permissions: Object.freeze([
    P.SELF_ANALYTICS_VIEW,
    P.SELF_ANALYTICS_MANAGE,
    P.SELF_CLEAR_CACHE_VIEW,
    P.SELF_CLEAR_CACHE_MANAGE,
    P.SELF_DEVELOPERS_VIEW,
    P.SELF_DEVELOPERS_MANAGE,
    P.SELF_IMAGE_SETTINGS_VIEW,
    P.SELF_IMAGE_SETTINGS_MANAGE,
    P.SELF_INTEGRATION_VIEW,
    P.SELF_INTEGRATION_MANAGE,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_PERFORMANCE_VIEW,
    P.SELF_PERFORMANCE_MANAGE,
    P.SELF_USAGE_VIEW,
    P.SELF_USAGE_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const accountant = Object.freeze({
  name: RoleId.ACCOUNTANT,
  description: 'Finance',
  helptext: 'Access to analytics and billing',
  permissions: Object.freeze([
    P.SELF_ANALYTICS_VIEW,
    P.SELF_ANALYTICS_MANAGE,
    P.SELF_BILLING_VIEW,
    P.SELF_BILLING_MANAGE,
    P.SELF_REFERRAL_VIEW,
    P.SELF_REFERRAL_MANAGE,
    P.SELF_USAGE_VIEW,
    P.SELF_USAGE_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const marketer = Object.freeze({
  name: RoleId.MARKETER,
  description: 'Media Library Full Access',
  helptext: 'Full media library access',
  permissions: Object.freeze([
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const marketerViewOnly = Object.freeze({
  name: RoleId.MARKETER_VIEW_ONLY,
  description: 'Media Library View Only Access',
  helptext: 'View-only media library access',
  permissions: Object.freeze([
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_MEDIA_LIBRARY_VIEW,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const restricted = Object.freeze({
  name: RoleId.RESTRICTED,
  description: 'Media Library Restricted Access',
  helptext: 'Restricted access to media library',
  permissions: Object.freeze([
    P.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
    P.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
    P.SELF_USER_VIEW,
    P.SELF_USER_MANAGE,
  ]),
});

const roles = Object.freeze({
  admin,
  client,
  accountViewer,
  viewer,
  agency,
  developer,
  accountant,
  marketer,
  marketerViewOnly,
  restricted,
});

/**
 * @param {number | string} roleId
 * @returns {typeof admin | undefined}
 */
function getRoleById(roleId) {
  const id = parseInt(roleId, 10);
  if (Number.isNaN(id)) {
    return undefined;
  }
  return Object.values(roles).find((role) => role.name === id);
}

module.exports = {
  RoleId,
  roles,
  getRoleById,
};
